import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { useAuth } from '../context/AuthContext';
import { helperFunction } from '../helpers/api_call_helper';
import { getSubscriptionPlans } from '../helpers/backend_helper';
import {
    getSubscriptionCheckoutPath,
    getSubscriptionMonths,
    getSubscriptionPlansPayload,
    normalizeSubscriptionPlan,
} from '../helpers/utils';

const getButtonText = (plan) => {
    const badge = String(plan.badge || '').toLowerCase();
    if (badge.includes('popular') || badge.includes('value')) return 'Best Value';
    if (badge.includes('deal')) return 'Join Now';
    return 'Buy Now';
};

const getPlanStyle = (plan) => {
    const badge = String(plan.badge || '').toLowerCase();
    const name = String(plan.name || '').toLowerCase();

    return {
        isGold: badge.includes('popular') || badge.includes('deal') || name.includes('half') || name.includes('annual'),
        isPopular: badge.includes('popular') || badge.includes('value'),
    };
};

const getPlanFeatures = (features) => {
    const featureList = Array.isArray(features) ? features : [features];

    return featureList.flatMap((feature) => {
        if (!feature) return [];

        const html = String(feature);
        if (!/<(p|li)\b/i.test(html) || typeof DOMParser === 'undefined') {
            return [html];
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const blocks = Array.from(doc.body.querySelectorAll('p, li'))
            .filter((node) => node.textContent.replace(/\u00a0/g, ' ').trim())
            .map((node) => node.innerHTML.trim());

        return blocks.length ? blocks : [html];
    });
};

const hasActiveSubscription = (user) => {
    if (!user || user.subscriptionType === 'free') return false;
    const expiry = user.subscriptionExpiryDate ? new Date(user.subscriptionExpiryDate) : null;
    return Boolean(user.isSubscribed && expiry && expiry.getTime() > Date.now());
};

export default function SubscriptionPlans() {
    const navigate = useNavigate();
    const { user, isLoggedIn, openAuthModal } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');

    useEffect(() => {
        let mounted = true;

        helperFunction(
            async () => {
                const response = await getSubscriptionPlans({ limit: 100 });
                const nextPlans = getSubscriptionPlansPayload(response)
                    .filter((plan) => plan?.isActive !== false)
                    .map((plan) => ({
                        ...normalizeSubscriptionPlan(plan),
                        features: getPlanFeatures(plan.features),
                    }));

                if (mounted) setPlans(nextPlans);
                return response;
            },
            setLoading,
            undefined,
            (message) => {
                if (mounted) setFetchError(message);
            },
        );

        return () => {
            mounted = false;
        };
    }, []);

    const handleSubscribe = (plan) => {
        if (!isLoggedIn) {
            openAuthModal();
            return;
        }

        navigate(getSubscriptionCheckoutPath(plan));
    };

    return (
        <section className="section subscription-section" id="subscriptions">
            <div className="section-header reveal in-view section-header-with-nav">
                <div className="header-content">
                    <div className="section-label">Premium Access</div>
                    <h2 className="section-title">Unlimited Reading Plans</h2>
                    <p className="section-subtitle">Get access to everything above with our affordable membership plans</p>
                </div>
                <div className="modern-slider-nav">
                    <button className="nav-btn plans-prev" aria-label="Previous subscription plan" type="button">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <button className="nav-btn plans-next" aria-label="Next subscription plan" type="button">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                </div>
            </div>

            <Swiper
                modules={[Pagination, Navigation]}
                spaceBetween={24}
                slidesPerView={1}
                navigation={{
                    prevEl: '.plans-prev',
                    nextEl: '.plans-next',
                }}
                pagination={{ clickable: true }}
                breakpoints={{
                    640: { slidesPerView: 2, spaceBetween: 20 },
                    1024: { slidesPerView: 3, spaceBetween: 24 }
                }}
                className="plans-swiper"
                style={{ padding: '1rem 0 3.5rem 0' }}
            >
                {loading && !plans.length && (
                    <SwiperSlide>
                        <div className="plan-card">
                            <h3 className="plan-name">Loading plans...</h3>
                        </div>
                    </SwiperSlide>
                )}

                {!loading && !plans.length && (
                    <SwiperSlide>
                        <div className="plan-card">
                            <h3 className="plan-name">No subscription plans available yet</h3>
                            <p className="plan-save">{fetchError || 'Plans will appear here once they are added.'}</p>
                        </div>
                    </SwiperSlide>
                )}

                {plans.map((plan) => {
                    const planStyle = getPlanStyle(plan);
                    const activeSubscription = hasActiveSubscription(user);
                    const currentRank = activeSubscription ? getSubscriptionMonths(user?.subscriptionType) : 0;
                    const planRank = getSubscriptionMonths(`${plan.name} ${plan.period}`);
                    const isCurrentPlan = activeSubscription && currentRank === planRank;
                    const blockedByHigherPlan = activeSubscription && currentRank > planRank;
                    const disableSubscribe = isCurrentPlan || blockedByHigherPlan;

                    return (
                        <SwiperSlide key={plan.id || plan.name}>
                            <div
                                className={`plan-card ${planStyle.isPopular ? 'plan-popular' : ''}`}
                            >
                                <div className="plan-card-head">
                                    <h3 className="plan-name">{plan.name}</h3>
                                    {plan.badge && <span className="plan-badge">{plan.badge}</span>}
                                </div>
                                <div className="plan-price">
                                    <span className="price-amount">Rs. {plan.price.toLocaleString()}</span>
                                    <span style={{ color: 'var(--text-dim)' }}>{plan.period}</span>
                                </div>
                                {plan.saveText && <p className="plan-save">{plan.saveText}</p>}
                                <ul className="plan-features">
                                    {plan.features.map((feature, fIdx) => (
                                        <li key={`${plan.id || plan.name}-${fIdx}`}>
                                            <div
                                                className="plan-feature-html"
                                                dangerouslySetInnerHTML={{ __html: feature }}
                                            />
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className={`btn-plan ${planStyle.isGold ? 'btn-plan-gold' : 'btn-plan-accent'}`}
                                    onClick={() => handleSubscribe(plan)}
                                    disabled={disableSubscribe}
                                    type="button"
                                >
                                    {isCurrentPlan ? 'Current Plan' : blockedByHigherPlan ? 'Upgrade Only' : getButtonText(plan)}
                                </button>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </section>
    );
}
